export const EXPORTED_STYLE_PROPERTIES = [
  'fill',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-dasharray',
  'stroke-linecap',
  'stroke-opacity',
  'opacity',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'text-anchor',
  'dominant-baseline',
] as const;

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

const GRAPH_CONTENT_SELECTOR =
  '[data-testid="node"], [data-testid="edge"], [data-testid="label"]';
const XLINK_NAMESPACE = 'http://www.w3.org/1999/xlink';

function copyComputedStyles(source: Element, target: Element): void {
  const view = source.ownerDocument?.defaultView;
  if (!view?.getComputedStyle) {
    return;
  }

  const computed = view.getComputedStyle(source);
  const declarations: string[] = [];
  for (const property of EXPORTED_STYLE_PROPERTIES) {
    const value = computed.getPropertyValue(property);
    if (value) {
      declarations.push(`${property}:${value}`);
    }
  }

  if (declarations.length > 0) {
    const existing = target.getAttribute('style');
    target.setAttribute(
      'style',
      existing
        ? `${declarations.join(';')};${existing}`
        : declarations.join(';'),
    );
  }
}

export function findExportableSvg(
  container: Element | null | undefined,
): SVGSVGElement | null {
  if (!container) {
    return null;
  }

  const candidates = Array.from(
    container.querySelectorAll<SVGSVGElement>('svg'),
  );
  if (candidates.length === 0) {
    return null;
  }

  const withGraphContent = candidates.find(svg =>
    svg.querySelector(GRAPH_CONTENT_SELECTOR),
  );
  if (withGraphContent) {
    return withGraphContent;
  }

  return candidates.reduce((largest, candidate) => {
    const area = ({ width, height }: { width: number; height: number }) =>
      width * height;
    return area(readSvgSize(candidate)) > area(readSvgSize(largest))
      ? candidate
      : largest;
  });
}

export function readSvgSize(svg: SVGSVGElement): {
  width: number;
  height: number;
} {
  const viewBox = svg.getAttribute('viewBox');
  if (viewBox) {
    const [, , width, height] = viewBox
      .split(/[\s,]+/)
      .map(part => Number(part));
    if (Number.isFinite(width) && Number.isFinite(height) && width > 0) {
      return { width, height };
    }
  }

  const rect = svg.getBoundingClientRect?.();
  return {
    width: Math.max(1, Math.round(rect?.width || 800)),
    height: Math.max(1, Math.round(rect?.height || 600)),
  };
}

export interface SerializeSvgOptions {
  background?: string;
}

export function serializeSvg(
  svg: SVGSVGElement,
  options: SerializeSvgOptions = {},
): string {
  const { width, height } = readSvgSize(svg);
  const clone = svg.cloneNode(true) as SVGSVGElement;

  clone.setAttribute('xmlns', SVG_NAMESPACE);
  clone.setAttribute('xmlns:xlink', XLINK_NAMESPACE);
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  if (!clone.getAttribute('viewBox')) {
    clone.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }
  clone.removeAttribute('style');

  copyComputedStyles(svg, clone);
  const sources = svg.querySelectorAll('*');
  const targets = clone.querySelectorAll('*');
  for (let index = 0; index < sources.length; index += 1) {
    const target = targets.item(index);
    if (target) {
      copyComputedStyles(sources.item(index), target);
    }
  }

  if (options.background) {
    const rect = clone.ownerDocument.createElementNS(SVG_NAMESPACE, 'rect');
    rect.setAttribute('width', String(width));
    rect.setAttribute('height', String(height));
    rect.setAttribute('fill', options.background);
    clone.insertBefore(rect, clone.firstChild);
  }

  return new XMLSerializer().serializeToString(clone);
}

export function exportFileName(...parts: (string | undefined)[]): string {
  return (
    parts
      .filter((part): part is string => Boolean(part && part.trim()))
      .join('-')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('en-US')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'c4-export'
  );
}

function triggerDownload(url: string, fileName: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export function downloadSvg(
  svg: SVGSVGElement,
  fileName: string,
  options: SerializeSvgOptions = {},
): void {
  const markup = serializeSvg(svg, options);
  const url = URL.createObjectURL(
    new Blob([markup], { type: 'image/svg+xml;charset=utf-8' }),
  );
  try {
    triggerDownload(url, `${fileName}.svg`);
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

export interface DownloadPngOptions extends SerializeSvgOptions {
  scale?: number;
}

export async function downloadPng(
  svg: SVGSVGElement,
  fileName: string,
  options: DownloadPngOptions = {},
): Promise<void> {
  const { scale = 2, ...serializeOptions } = options;
  const { width, height } = readSvgSize(svg);
  const markup = serializeSvg(svg, serializeOptions);
  const source = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    markup,
  )}`;

  const image = new Image();
  image.width = width;
  image.height = height;

  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Falha ao rasterizar o SVG.'));
    image.src = source;
  });

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas 2D indisponível neste navegador.');
  }
  context.scale(scale, scale);
  context.drawImage(image, 0, 0);

  triggerDownload(canvas.toDataURL('image/png'), `${fileName}.png`);
}
